import { isInvoicingSessionCancelled } from "components/Dashboard/DoctorDashboard/utils/isInvoicingSessionCancelled";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import ConfirmModal from "components/confirm-modal";
import { useSessionTypes } from "contexts/SessionTypesContext";
import useLocationParams from "hooks/useLocationParams";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import { useDispatch, useSelector } from "react-redux";
import {
  SidesheetType,
  selectCurrentSidesheet,
  selectFocusedSessionAppointment,
  selectFocusedSessionNextStatus,
  showSessionDetails,
} from "store/calendarSidebarSlice";
import { AmplitudePageName } from "utils/amplitude";
import {
  getAppointmentSessionTypeColor,
  getAppointmentTitle,
  getSessionIndicatorIconsDataForAppointment,
  isAppointmentCancelled,
} from "utils/calendar-event-utils/appointment-utils";
import canJoinSessionForPro from "utils/canJoinSessionForPro";
import getClasses from "utils/getClasses";
import { useCalendarSidebarContext } from "../../../../../calendar-sidebar/context/CalendarSidebarContext";
import { useCalendarTimeline } from "../../../../contexts/calendar-timeline-context/CalendarTimelineContextProvider";
import { SessionIndicatorIconsData } from "../../../session-indicator-icons/SessionIndicatorIcons";
import { CalendarColumnPosition } from "../../utils/splitEventsIntoColumns";
import TimeslotButton from "../timeslot-button/TimeslotButton";
import styles from "./AppointmentTimeslot.module.scss";
import useCalendarAppointmentDrag from "./hooks/useCalendarAppointmentDrag";

const OVERLAP_ERROR_MESSAGE =
  "failed to reschedule appointment: overlap in your appointments";

interface AppointmentTimeslotProps {
  appointment: AppointmentWithSeriesInfo & {
    invoice?: InvoiceData;
  };
  isDndPreview?: boolean;
  isInvalidDndPreview?: boolean;
  amplitudePageName: AmplitudePageName;
  date?: Date;
  calendarColumnPosition?: CalendarColumnPosition;
}

const AppointmentTimeslot = ({
  appointment,
  isInvalidDndPreview,
  isDndPreview,
  amplitudePageName,
  date,
  calendarColumnPosition,
}: AppointmentTimeslotProps) => {
  const dispatch = useDispatch();
  const { getParam } = useLocationParams();
  const currentSidesheet = useSelector(selectCurrentSidesheet);
  const { nowValue } = useCalendarTimeline();
  const { sessionTypes } = useSessionTypes();

  const { currentCalendarTimezone } = useCalendarTime();
  const { start, end } = useMemo(() => {
    const start = moment.tz(
      appointment.appointment_start,
      currentCalendarTimezone,
    );
    const end = moment.tz(appointment.appointment_end, currentCalendarTimezone);
    return { start, end };
  }, [appointment, currentCalendarTimezone]);

  const active = useMemo(
    () => +start <= nowValue && nowValue <= +end,
    [start, nowValue, end],
  );

  const canJoin = canJoinSessionForPro(start.valueOf(), end.valueOf());
  const isCancelled = appointment.statusFlags.cancelledOrAbandoned;
  const someoneBooking =
    appointment.statusFlags.pendingClientPortalApproval ||
    appointment.statusFlags.pendingClientPortalSignupComplete;

  const focusedSessionAppointment = useSelector(
    selectFocusedSessionAppointment,
  );
  const nextStatus = useSelector(selectFocusedSessionNextStatus);
  const isFocusedSession =
    focusedSessionAppointment?.appointment_id === appointment.appointment_id;

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sessionIdParam = getParam("session_id");
    if (appointment.appointment_id.toString() !== sessionIdParam) {
      return;
    }
    scrollRef.current?.scrollIntoView({
      block: "center",
    });
  }, [getParam, appointment.appointment_id]);

  const canReschedule =
    start.toDate().getTime() > Date.now() &&
    (appointment.statusFlags.usingInvoicing
      ? !isInvoicingSessionCancelled(appointment, null)
      : !canJoin &&
        !isCancelled &&
        !appointment.appointment_series?.repeat_rule);

  const {
    show: showRescheduleErrorModal,
    open: openRescheduleErrorModal,
    close: closeRescheduleErrorModal,
  } = useToggleModal(false);

  const {
    show: showConfirmDndModal,
    open: openConfirmDndModal,
    close: closeConfirmDndModal,
  } = useToggleModal(false);

  const [rescheduleErrorMessage, setRescheduleErrorMessage] =
    useState<string>("");

  const handleRescheduleError = useCallback(
    (message: string) => {
      setRescheduleErrorMessage(message);
      openRescheduleErrorModal();
    },
    [openRescheduleErrorModal],
  );

  const {
    drag,
    dragPreview,
    isDragging,
    isAnotherAppointmentBeingDragged,
    isBeingRescheduledViaDnd,
    onConfirmDnd,
    onCancelDnd,
  } = useCalendarAppointmentDrag({
    appointment,
    handleRescheduleError,
    openConfirmDndModal,
  });

  const { requestCancelAddNewSession } = useCalendarSidebarContext();

  const showCurrentSessionDetails = useCallback(
    () => dispatch(showSessionDetails({ appointment, nextStatus })),
    [appointment, dispatch, nextStatus],
  );

  const onClick = useCallback(() => {
    if (
      currentSidesheet === SidesheetType.AddToCalendar &&
      requestCancelAddNewSession
    ) {
      requestCancelAddNewSession(showCurrentSessionDetails);
    } else {
      showCurrentSessionDetails();
    }
  }, [currentSidesheet, requestCancelAddNewSession, showCurrentSessionDetails]);

  const displayAsCancelled = useMemo(
    () =>
      isAppointmentCancelled(appointment, isFocusedSession ? nextStatus : null),
    [appointment, isFocusedSession, nextStatus],
  );

  const backgroundColor = useMemo(() => {
    if (someoneBooking) {
      return "#ffffff";
    }

    return getAppointmentSessionTypeColor(appointment, sessionTypes);
  }, [appointment, sessionTypes, someoneBooking]);

  const timeslotName = useMemo(() => {
    if (someoneBooking) {
      return "Someone is booking from Client Portal";
    }

    return getAppointmentTitle(appointment);
  }, [appointment, someoneBooking]);

  const typeIndicatorColor = useMemo(() => {
    if (someoneBooking) {
      return "";
    }

    if (displayAsCancelled) {
      return "#fb545e";
    }

    if (active) {
      return "#2073b3";
    }

    return "#a3c5ff";
  }, [someoneBooking, displayAsCancelled, active]);

  const displayAsInvited = useMemo(
    () =>
      (!isCancelled && appointment.statusFlags.unconfirmedInvite) ||
      appointment.statusFlags.needsPayment,
    [
      isCancelled,
      appointment.statusFlags.unconfirmedInvite,
      appointment.statusFlags.needsPayment,
    ],
  );

  const sessionIndicatorIconsData: SessionIndicatorIconsData = useMemo(
    () => getSessionIndicatorIconsDataForAppointment(appointment),
    [appointment],
  );

  return (
    <>
      <div ref={dragPreview} className={styles["drag-preview"]} />
      <TimeslotButton
        ref={mergeRefs([scrollRef, canReschedule ? drag : null])}
        onClick={onClick}
        className={
          someoneBooking
            ? styles["appointment-timeslot--someone-booking"]
            : getClasses(
                styles["appointment-timeslot"],
                displayAsInvited && styles["appointment-timeslot--invited"],
                isDragging && styles["appointment-timeslot--dragging"],
                isDndPreview && styles["appointment-timeslot--preview"],
                isInvalidDndPreview &&
                  styles["appointment-timeslot--preview--invalid"],
              )
        }
        style={{
          ...(isAnotherAppointmentBeingDragged ? { zIndex: 1 } : {}),
          visibility:
            isBeingRescheduledViaDnd || showConfirmDndModal
              ? "hidden"
              : "visible",
          backgroundColor,
          // opacity: new Date(appointment.appointment_end).valueOf() < Date.now() ? 0.5 : 1,
        }}
        startDate={appointment.appointment_start}
        endDate={appointment.appointment_end}
        typeIndicatorColor={typeIndicatorColor}
        timeslotName={timeslotName}
        isFocused={isFocusedSession}
        isCancelled={displayAsCancelled}
        sessionIndicatorIconsData={sessionIndicatorIconsData}
        date={date}
        calendarColumnPosition={
          isDndPreview ? undefined : calendarColumnPosition
        }
      />
      <ConfirmModal
        show={showRescheduleErrorModal}
        onConfirm={() => {
          closeRescheduleErrorModal();
          setRescheduleErrorMessage("");
        }}
        onCancel={() => {
          closeRescheduleErrorModal();
          setRescheduleErrorMessage("");
        }}
        labelTitle={
          rescheduleErrorMessage === OVERLAP_ERROR_MESSAGE
            ? messages.oopsConflictingSession
            : messages.oops
        }
        labelCancel={messages.cancelTheMove}
        labelConfirm={messages.reschedule}
      >
        {rescheduleErrorMessage === OVERLAP_ERROR_MESSAGE
          ? messages.thereIsConflictWithExisitngSession
          : rescheduleErrorMessage}
      </ConfirmModal>
      <ConfirmModal
        show={showConfirmDndModal}
        onConfirm={() => {
          if (onConfirmDnd) {
            onConfirmDnd();
          }
          closeConfirmDndModal();
        }}
        onCancel={() => {
          if (onCancelDnd) {
            onCancelDnd();
          }
          closeConfirmDndModal();
        }}
        labelTitle={messages.readyToReschedule}
        labelCancel={messages.cancel}
        labelConfirm={messages.reschedule}
      >
        If you reschedule this session, <b>please message your client</b> to
        notify them about this update. Orchid will not automatically notify them
        for you.
      </ConfirmModal>
    </>
  );
};

export default AppointmentTimeslot;
